import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';
import { FaCheckCircle, FaFilePdf, FaRegEdit, FaTimes } from 'react-icons/fa';
import { LuMail } from 'react-icons/lu';
import { useAuth } from '../../contexts/auth';
import api from '../../services/api';
import { toMask } from '../../services/currency';
import Card from '../Card/Card';
import InfoPerson from '../Person/InfoPerson';
import {
  EstimateEquipment,
  EstimateProduct,
  EstimateService,
  EstimateViewModel,
} from '../../models/estimate/EstimateViewModel';
import EstimateEquipments from './EstimateEquipments';
import EstimateServices from './EstimateServices';
import EstimateProducts from './EstimateProducts';
import EstimateComments, { EstimateCommentType } from './EstimateComments';
import EstimateStatusBadge from './EstimateStatusBadge';

import styles from './EstimateForm.module.scss';
import EstimateActionButtons from './EstimateActionButtons';
import EstimateInvoices from './EstimateInvoices';
import CustomButton from '../CustomButton/CustomButton';
import EstimateStatus from '../../enums/EstimateStatus';
import EstimateRemiders from './EstimateReminders';
import EstimateTags from './EstimateTags';
import EstimateEmailTo from './EstimateEmailTo';
import SentEstimateEmailList from './SentEstimateEmailList';
import EstimateAttachments from './EstimateAttachments';

interface EstimateFooProps {
  isLoading: boolean;
  estimate: EstimateViewModel;
  refreshEstimate: () => void;
  onSubmit: (data: any) => void;
}

export default function EstimateForm({
  isLoading,
  estimate,
  refreshEstimate,
  onSubmit,
}: EstimateFooProps) {
  const {
    user: { organization },
    isAdmin,
  } = useAuth();

  const { register, getValues, reset } = useForm();
  const [activeTab, setActiveTab] = useState(0);
  const [isEmailOpenModal, setIsEmailOpenModal] = useState(false);

  const [estimateProducts, setEstimateProducts] = useState<EstimateProduct[]>(
    estimate.estimateProducts
  );
  const [estimateServices, setEstimateServices] = useState<EstimateService[]>(
    estimate.estimateServices
  );
  const [estimateEquipments, setEstimateEquipments] = useState<
    EstimateEquipment[]
  >(estimate.estimateEquipments);

  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isHideDiscount, setIsHideDiscount] = useState(true);
  const [formErrors, setFormErrors] = useState<Record<string, any>>([]);

  useEffect(() => {
    setEstimateProducts(estimate.estimateProducts);
    setEstimateServices(estimate.estimateServices);
    setEstimateEquipments(estimate.estimateEquipments);
    reset({
      discount: estimate.discount,
    });
  }, [estimate]);

  const removeProduct = useCallback(
    async (estimateProduct: EstimateProduct) => {
      try {
        if (
          // eslint-disable-next-line no-restricted-globals
          confirm(
            `Você tem certeza que deseja remover o produto "${estimateProduct.name}"? Essa ação não poderá ser desfeita.`
          )
        ) {
          await api.delete(
            `organizations/${organization.id}/estimates/${estimate.id}/products/${estimateProduct.productId}`
          );
          refreshEstimate();
          toast.success(
            `O produto "${estimateProduct.name}" foi removido do Orçamento`
          );
        }
      } catch (e) {
        // TODO: Recarregar lista
        toast.error(
          'Houve um problema ao tentar remover o produto do Orçamento. Tente novamente.'
        );
      }
    },
    [organization.id, estimate.id]
  );

  const removeService = useCallback(
    async (estimateService: EstimateService) => {
      try {
        if (
          // eslint-disable-next-line no-restricted-globals
          confirm(
            `Você tem certeza que deseja remover o serviço "${estimateService.name}"? Essa ação não poderá ser desfeita.`
          )
        ) {
          await api.delete(
            `organizations/${organization.id}/estimates/${estimate.id}/services/${estimateService.serviceId}`
          );
          refreshEstimate();
          toast.success(
            `O serviço "${estimateService.name}" foi removido do Orçamento`
          );
        }
      } catch (e) {
        // TODO: Recarregar lista
        toast.error(
          'Houve um problema ao tentar remover o serviço do Orçamento. Tente novamente.'
        );
      }
    },
    [organization.id, estimate.id, refreshEstimate, toast]
  );

  const removeEquipment = useCallback(
    async (estimateEquipment: EstimateEquipment) => {
      try {
        const displayName = `${estimateEquipment.name} (N/S: ${estimateEquipment.serialNumber})`;
        if (
          // eslint-disable-next-line no-restricted-globals
          confirm(
            `Você tem certeza que deseja remover o equipamento "${displayName}"? Essa ação não poderá ser desfeita.`
          )
        ) {
          await api.delete(
            `organizations/${organization.id}/estimates/${estimate.id}/equipments/${estimateEquipment.equipmentId}`
          );
          refreshEstimate();
          toast.success(
            `O equipamento "${displayName}" foi removido do Orçamento`
          );
        }
      } catch (e) {
        // TODO: Recarregar lista
        toast.error(
          'Houve um problema ao tentar remover o equipamento do Orçamento. Tente novamente.'
        );
      }
    },
    [organization.id, estimate.id, refreshEstimate, toast]
  );

  const generatePdf = async () => {
    const { data: url } = await api.post(
      `organizations/${organization.id}/estimates/${estimate.id}/pdf`
    );
    // @ts-ignore
    window.open(url, '_blank', 'noopener,noreferrer').focus();
  };

  async function setDiscount(discount: number) {
    try {
      await api.post(
        `organizations/${organization.id}/estimates/${estimate.id}/discount`,
        { discount }
      );
      toast.success('Desconto registrado com sucesso');
      refreshEstimate();
      setIsHideDiscount(true);
    } catch (e: any) {
      if (e?.response?.data?.errors) {
        if (e?.response?.status === 422) {
          setFormErrors(e.response.data.errors);
        } else {
          toast.error(
            e?.response?.data?.errors.reduce(
              (acc: string, error: string) => acc + error,
              ''
            )
          );
        }
      } else {
        throw e;
      }
    }
  }

  const addDiscount = async () => {
    const { discount } = getValues();
    await setDiscount(discount);
  };

  const removeDiscount = async () => {
    await setDiscount(0);
  };

  return (
    <div>
      <ul className='nav nav-tabs'>
        <li className='nav-item'>
          <button
            type='button'
            className={`nav-link ${activeTab === 0 ? 'active' : ''}`}
            aria-current='page'
            onClick={() => setActiveTab(0)}
          >
            Dados
          </button>
        </li>
        <li className='nav-item '>
          <button
            type='button'
            className={`nav-link ${activeTab === 1 ? 'active' : ''}`}
            onClick={() => setActiveTab(1)}
          >
            Ordens de Serviço
          </button>
        </li>
        <li className='nav-item '>
          <button
            type='button'
            className={`nav-link ${activeTab === 2 ? 'active' : ''}`}
            onClick={() => setActiveTab(2)}
          >
            E-mails enviados
          </button>
        </li>
      </ul>

      {activeTab === 0 && (
        <>
          <div className='d-flex align-items-center justify-content-between'>
            <h1>Nº {estimate.number ? estimate.number : '#'}</h1>
            <div>
              <div className={`${styles.estimateHeaderBadges}`}>
                <EstimateStatusBadge status={estimate.status} />
              </div>
            </div>
          </div>
          <div className='d-flex justify-content-between border-top text-muted'>
            <div className='d-flex flex-column'>
              <span>{estimate.createdAt.toLocaleString()}</span>
              <span>{estimate.transactionNatureName}</span>
            </div>
            <div className='d-flex flex-column'>
              <span>
                Última atualização: {estimate.updatedAt.toLocaleString()}
              </span>
            </div>
          </div>

          <div className='row justify-content-evenly mt-5 gap-3'>
            <div className='col-md-5'>
              <InfoPerson
                title='Empresa'
                name={estimate.companyName}
                identifier={estimate.companyIdentifier}
                identifierType={estimate.companyType}
                editAction={`/companies/${estimate.companyId}/edit`}
              />
            </div>
            <div className='col-md-5'>
              <InfoPerson
                title='Cliente'
                name={estimate.customerName}
                identifier={estimate.customerIdentifier}
                identifierType={estimate.customerType}
                editAction={`/customers/${estimate.customerId}/edit`}
              />
            </div>
          </div>

          <div className='mt-5'>
            <div className='mt-5'>
              <EstimateEquipments
                estimate={estimate}
                items={estimateEquipments}
                refreshEquipments={refreshEstimate}
                removeEquipment={removeEquipment}
              />
            </div>
            <div className='mt-5'>
              <EstimateServices
                estimate={estimate}
                items={estimateServices}
                refreshServices={refreshEstimate}
                removeService={removeService}
              />
            </div>
            <div className='mt-5'>
              <EstimateProducts
                estimate={estimate}
                items={estimateProducts}
                refreshProducts={refreshEstimate}
                removeProduct={removeProduct}
              />
            </div>
            <div className='mt-3'>
              <EstimateAttachments estimateId={estimate.id} />
            </div>
            <div className='mt-3'>
              <EstimateTags
                estimateId={estimate.id}
                items={estimate.estimateTags}
                refreshTags={refreshEstimate}
              />
            </div>
            <hr />
            <div className='row gap-2'>
              <div className='col-sm'>
                <Card header='Total e desconto' isLoading={false}>
                  <div className='d-flex flex-column mt-2 fs-4'>
                    <div>
                      <span className='fw-bold'>Subtotal</span>:{' '}
                      {toMask(estimate.subTotal)}
                    </div>
                    {isHideDiscount && (
                      <div className='d-flex align-items-center'>
                        <span className='fw-bold'>Desconto</span>:{' '}
                        {toMask(estimate.discount)}
                        <button
                          type='button'
                          className='btn p-0 fs-4 mb-1 ms-2'
                          onClick={() => setIsHideDiscount(false)}
                        >
                          <FaRegEdit />
                        </button>
                      </div>
                    )}
                    {!isHideDiscount && (
                      <div className='d-flex align-items-center gap-2'>
                        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                        <label
                          htmlFor='inputLabel'
                          className='form-label fw-bold'
                        >
                          Desconto:
                        </label>
                        <div
                          className='input-group h-75 input-group-sm'
                          style={{ width: '7rem' }}
                        >
                          <span className='input-group-text'>R$</span>
                          <input
                            {...register('discount')}
                            className='form-control'
                            placeholder='Desconto'
                            type='number'
                            min='.01'
                            step='.01'
                            required
                          />
                        </div>
                        <div className='d-flex align-items-center align-self-end gap-2 mb-2'>
                          <button
                            type='button'
                            className='btn btn-sm btn-success'
                            onClick={() => addDiscount()}
                          >
                            <FaCheckCircle />
                          </button>
                          <button
                            type='button'
                            className='btn btn-sm btn-danger'
                            onClick={() => removeDiscount()}
                          >
                            <FaTimes />
                          </button>
                        </div>
                      </div>
                    )}

                    <div>
                      <span className='fw-bold'>Total</span>:{' '}
                      {toMask(estimate.total)}
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>

          <div className='mt-1'>
            <EstimateComments
              estimateId={estimate.id}
              type={EstimateCommentType.General}
            />
          </div>

          <div className='mt-1'>
            <EstimateComments
              estimateId={estimate.id}
              type={EstimateCommentType.Internal}
            />
          </div>
          <div className='mt-3'>
            <EstimateRemiders estimateId={estimate.id} />
          </div>
          <hr />

          <div className='mt-1 mb-5 p-2 d-flex justify-content-between align-items-center flex-wrap gap-2'>
            <div className='btn-left d-flex justify-content-end gap-2'>
              {estimate.status !== EstimateStatus.Draft &&
                estimate.status !== EstimateStatus.Canceled && (
                  <CustomButton
                    className='btn btn-danger'
                    type='button'
                    handleClick={generatePdf}
                  >
                    PDF <FaFilePdf />
                  </CustomButton>
                )}
              <button
                className='btn btn-primary'
                type='button'
                onClick={() => setIsEmailOpenModal(true)}
              >
                E-mail <LuMail />
              </button>
            </div>
            <div className='btn-right'>
              <EstimateActionButtons
                estimate={estimate}
                refreshEstimate={refreshEstimate}
              />
            </div>
          </div>
        </>
      )}

      <EstimateEmailTo
        estimate={estimate}
        isOpen={isEmailOpenModal}
        setIsOpenModal={setIsEmailOpenModal}
        refreshEstimate={refreshEstimate}
      />

      {activeTab === 1 && <EstimateInvoices estimateId={estimate.id} />}
      {activeTab === 2 && <SentEstimateEmailList estimate={estimate} />}
    </div>
  );
}
